import {
  canEditMasterData as sharedCanEditMasterData,
  canEditPromotions as sharedCanEditPromotions,
  canEditSystemConfig as sharedCanEditSystemConfig,
  getVisibleBranchIds,
} from '@pos/domain';
import type { PermissionOverride, Role } from '@pos/types';

export type BoRole = Extract<Role, 'owner' | 'manager' | 'staff' | 'cashier'>;
export interface BoUser {
  id: string;
  name: string;
  role: BoRole;
  branchIds: string[];
  permissionOverrides?: PermissionOverride[];
}

/** Owner → all branches; Manager → assigned branchIds (intersection with existing). */
export const visibleBranchIds = (
  user: BoUser,
  allBranchIds: string[],
): string[] => getVisibleBranchIds(user, allBranchIds);

const LIMITED_READ_ROUTES = new Set(['/stock', '/products', '/movements']);

const normalizePath = (path: string): string =>
  path.length > 1 && path.endsWith('/') ? path.replace(/\/+$/, '') : path;

const isLimitedReadRoute = (path: string): boolean => {
  const normalized = normalizePath(path);
  return (
    LIMITED_READ_ROUTES.has(normalized) || normalized.startsWith('/products/')
  );
};

export const canAccessDashboardRoute = (
  user: BoUser,
  path: string,
): boolean => {
  if (user.role === 'owner' || user.role === 'manager') {
    return true;
  }

  if (user.role === 'staff') {
    return isLimitedReadRoute(path) || normalizePath(path) === '/goods-receive';
  }

  if (user.role === 'cashier') {
    return isLimitedReadRoute(path);
  }

  return false;
};

export const defaultDashboardRoute = (user: BoUser): string => {
  if (user.role === 'staff' || user.role === 'cashier') {
    return '/stock';
  }
  return '/';
};

export const canSeeAllBranches = (user: BoUser): boolean =>
  user.role === 'owner';
export const canEditMasterData = (user: BoUser): boolean =>
  sharedCanEditMasterData(user);
export const canEditPromotions = (user: BoUser): boolean =>
  sharedCanEditPromotions(user);
export const canEditSystemConfig = (user: BoUser): boolean =>
  sharedCanEditSystemConfig(user);
